import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TOPMATE_URL = 'https://topmate.io/acharyaji';

const questions = [
  {
    q: 'How long have you been thinking about this decision?',
    opts: ['A few days', 'Several weeks', 'Many months'],
    scores: [1, 2, 0],
  },
  {
    q: "Does the decision involve other people's expectations or pressure?",
    opts: ['Minimally', 'Somewhat', 'Significantly'],
    scores: [0, 1, 2],
  },
  {
    q: 'Have new information or circumstances recently changed the situation?',
    opts: ['Not really', 'Somewhat', 'Yes, significantly'],
    scores: [0, 1, 2],
  },
  {
    q: 'Do you feel mentally calm when thinking about this decision?',
    opts: ['Mostly calm', 'Somewhat unsettled', 'Quite unsettled'],
    scores: [0, 1, 2],
  },
  {
    q: 'Does the decision affect long-term stability?',
    opts: ['Minimal impact', 'Moderate impact', 'Significant impact'],
    scores: [0, 1, 2],
  },
];

const RESULTS = {
  low: {
    label: 'Decision Clarity Window',
    color: 'border-stone-200 bg-stone-50/80',
    accent: 'text-brand-dark',
    dot: 'bg-emerald-400',
    desc: 'The responses suggest a relatively settled phase. The decision appears to be approaching from a position of reasonable clarity. This can be a productive window for commitment, provided the foundational information is complete.',
    note: 'Clarity is present. The question is whether the timing is also aligned.',
  },
  mid: {
    label: 'Information Gathering Phase',
    color: 'border-stone-200 bg-stone-50/80',
    accent: 'text-brand-dark',
    dot: 'bg-amber-400',
    desc: 'The responses suggest that some elements of the decision are still in motion. This is a natural phase where gathering additional perspective, consulting relevant people, and allowing more information to emerge can improve the quality of the eventual decision.',
    note: 'Patience here is not hesitation. It is preparation.',
  },
  high: {
    label: 'Timing Friction Phase',
    color: 'border-stone-200 bg-stone-50/80',
    accent: 'text-brand-dark',
    dot: 'bg-rose-400',
    desc: 'The responses suggest a phase where external pressure, incomplete information, or internal unsettledness may be influencing the decision timeline. Decisions made under significant friction often require revisiting. A period of reflection, or a grounded external perspective, can be genuinely valuable here.',
    note: 'The decision may still be the right one. The timing may need more consideration.',
  },
};

export default function ShouldYouDecide() {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const handleAnswer = (score) => {
    const newAnswers = [...answers, score];
    if (current < questions.length - 1) {
      setAnswers(newAnswers);
      setCurrent(current + 1);
    } else {
      const total = newAnswers.reduce((a, b) => a + b, 0);
      const max = questions.length * 2;
      const pct = total / max;
      setResult(pct < 0.35 ? 'low' : pct < 0.65 ? 'mid' : 'high');
      setAnswers(newAnswers);
    }
  };

  const reset = () => { setCurrent(0); setAnswers([]); setResult(null); };
  const progress = result ? 100 : (current / questions.length) * 100;

  return (
    <section id="reflect" className="py-28 px-6 md:px-12" style={{ backgroundColor: '#F5EFE6' }}>
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center mb-16"
        >
          <p className="text-brand-gold text-[10px] uppercase tracking-[0.3em] font-semibold mb-4">
            A Moment of Reflection
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-brand-dark mb-5">
            Should You Decide Now?
          </h2>
          <p className="text-stone-500 font-light text-lg max-w-lg mx-auto leading-relaxed">
            Important decisions are influenced by clarity, timing, and external pressure. This short reflection helps you understand the phase you may currently be in.
          </p>
        </motion.div>

        {/* Progress bar */}
        <div className="w-full bg-stone-200/60 rounded-full h-px mb-12">
          <motion.div
            className="bg-brand-gold h-px rounded-full"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        </div>

        <AnimatePresence mode="wait">
          {!result ? (
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45 }}
              className="bg-white border border-stone-200/80 rounded-sm p-8 md:p-12 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-10">
                <span className="text-brand-gold/60 font-serif text-sm">0{current + 1}</span>
                <div className="flex-1 h-px bg-stone-100"></div>
                <span className="text-stone-300 text-xs font-light">{current + 1} of {questions.length}</span>
              </div>

              <h3 className="font-serif text-2xl text-brand-dark mb-10 leading-snug">
                {questions[current].q}
              </h3>

              <div className="space-y-3">
                {questions[current].opts.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => handleAnswer(questions[current].scores[i])}
                    className="w-full text-left px-6 py-4 border border-stone-150 bg-stone-50/50 rounded-sm hover:border-brand-gold/40 hover:bg-brand-gold/4 transition-all duration-300 text-stone-600 font-light group"
                  >
                    <span className="text-brand-gold/30 font-serif mr-3 text-sm group-hover:text-brand-gold/60 transition-colors">
                      {String.fromCharCode(65 + i)}.
                    </span>
                    {opt}
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.99 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className={`border rounded-sm p-8 md:p-12 bg-white shadow-sm`}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className={`w-2 h-2 rounded-full ${RESULTS[result].dot}`}></span>
                <p className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold">
                  Your Current Phase
                </p>
              </div>

              <h3 className="font-serif text-2xl text-brand-dark mb-6">
                {RESULTS[result].label}
              </h3>

              <div className="w-full h-px bg-stone-100 mb-7"></div>

              <p className="text-stone-500 font-light leading-relaxed mb-7 text-base">
                {RESULTS[result].desc}
              </p>

              <div className="border-l-2 border-brand-gold/30 pl-5 mb-10">
                <p className="text-stone-400 font-light text-sm italic leading-relaxed">
                  {RESULTS[result].note}
                </p>
              </div>

              <div className="space-y-4">
                <a
                  href={TOPMATE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full block py-5 bg-brand-dark text-brand-light font-medium text-center hover:bg-brand-goldDark transition-all duration-400 rounded-sm tracking-wide text-sm"
                >
                  Schedule Clarity Consultation
                </a>
                <p className="text-center text-xs text-stone-400 font-light">
                  A deeper conversation can help you understand the timing and influences around important decisions.
                </p>
                <button
                  onClick={reset}
                  className="w-full py-3 text-stone-400 font-light text-sm hover:text-stone-500 transition-all"
                >
                  Begin Again
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}